import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SupplierCard = ({ supplier, onAssignRole, onManageCapacity }) => {
  const isDualRole = supplier?.roleAssignments?.some(ra => ra?.role?.canBeClient) &&
                     supplier?.roleAssignments?.some(ra => ra?.role?.canBeSupplier);

  const getRoleBadgeColor = (roleType) => {
    switch (roleType) {
      case 'production_supplier':
        return 'bg-blue-100 text-blue-800';
      case 'dentist_supplier':
        return 'bg-purple-100 text-purple-800';
      case 'dentist_client':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const utilizationRate = supplier?.capacity?.maxCapacity > 0
    ? ((supplier?.capacity?.currentWorkload / supplier?.capacity?.maxCapacity) * 100)?.toFixed(0)
    : 0;

  const getUtilizationColor = (rate) => {
    if (rate >= 90) return 'text-red-600';
    if (rate >= 70) return 'text-orange-600';
    return 'text-green-600';
  };

  return (
    <div className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow p-6">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-lg font-semibold text-gray-900">
              {supplier?.companyName || supplier?.fullName}
            </h3>
            {isDualRole && (
              <span className="bg-gradient-to-r from-blue-500 to-purple-500 text-white text-xs px-2 py-1 rounded-full">
                Rôle Double
              </span>
            )}
          </div>
          <p className="text-sm text-gray-600">{supplier?.fullName}</p>
        </div>
        <div className={`w-3 h-3 rounded-full ${supplier?.isActive ? 'bg-green-500' : 'bg-gray-400'}`} />
      </div>

      {/* Contact Info */}
      <div className="space-y-2 mb-4">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Icon name="Mail" size={16} />
          <span className="truncate">{supplier?.email}</span>
        </div>
        {supplier?.phone && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Icon name="Phone" size={16} />
            <span>{supplier?.phone}</span>
          </div>
        )}
      </div>

      {/* Roles */}
      <div className="mb-4">
        <p className="text-xs text-gray-500 mb-2">Rôles:</p>
        <div className="flex flex-wrap gap-2">
          {supplier?.roleAssignments?.length > 0 ? (
            supplier?.roleAssignments?.map(ra => (
              <span
                key={ra?.id}
                className={`text-xs px-2 py-1 rounded-full ${getRoleBadgeColor(ra?.role?.roleType)}`}
              >
                {ra?.role?.roleName}
              </span>
            ))
          ) : (
            <span className="text-xs text-gray-400">Aucun rôle assigné</span>
          )}
        </div>
      </div>

      {/* Capacity Info */}
      {supplier?.capacity && (
        <div className="bg-gray-50 rounded-lg p-3 mb-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-xs text-gray-500">Machines</p>
              <p className="text-sm font-semibold text-gray-900">
                {supplier?.capacity?.activeMachines}/{supplier?.capacity?.totalMachines}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Utilisation</p>
              <p className={`text-sm font-semibold ${getUtilizationColor(utilizationRate)}`}>
                {utilizationRate}%
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Qualité</p>
              <div className="flex items-center gap-1">
                <Icon name="Star" size={14} className="text-yellow-500" />
                <p className="text-sm font-semibold text-gray-900">
                  {supplier?.capacity?.qualityScore?.toFixed(1) || 0}/5
                </p>
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-500">Livraison</p>
              <p className="text-sm font-semibold text-gray-900">
                {supplier?.capacity?.deliveryTimeAvg || 0}j
              </p>
            </div>
          </div>

          {/* Geographic Coverage */}
          {supplier?.capacity?.geographicCoverage?.length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <p className="text-xs text-gray-500 mb-1">Couverture géographique:</p>
              <div className="flex flex-wrap gap-1">
                {supplier?.capacity?.geographicCoverage?.slice(0, 3)?.map((country, idx) => (
                  <span key={idx} className="text-xs bg-white px-2 py-1 rounded border border-gray-200">
                    {country}
                  </span>
                ))}
                {supplier?.capacity?.geographicCoverage?.length > 3 && (
                  <span className="text-xs text-gray-500">+{supplier?.capacity?.geographicCoverage?.length - 3}</span>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={onAssignRole}
          className="flex-1"
        >
          <Icon name="UserPlus" size={16} />
          <span className="ml-1">Rôles</span>
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={onManageCapacity}
          className="flex-1"
        >
          <Icon name="Settings" size={16} />
          <span className="ml-1">Capacité</span>
        </Button>
      </div>
    </div>
  );
};

export default SupplierCard;